import React, { useEffect, useState } from 'react';
import { Box, Card, Link, Typography } from '@mui/material';
import Row from './Layout/Row';
import Column from './Layout/Column';
import { getDomainName, getLogoUrl, getUserNameFromUrl } from '../utils/SocialMediaUtils';
import { getC2paCategorizedActions, parseGenerator, selectGenerativeInfo, selectGenerativeSoftwareAgents, selectGenerativeType, selectProducer, selectSocialAccounts } from 'c2pa';
import { useThumbnailUrl } from '@contentauth/react';
import { useTranslation } from 'react-i18next';

// parseGenerator, selectEditsAndActivity, selectFormattedGenerator, selectGenerativeInfo, selectGenerativeSoftwareAgents, selectGenerativeType, selectSocialAccounts)

const SectionTitle = ({ title }) => {
    return (
        <Typography style={{
            fontSize: '0.8rem',
            fontWeight: '700',
            letterSpacing: '-0.5px',
            fontFamily: 'Inter, sans-serif',
            lineHeight: '1.41',
            color: '#000000',
        }}>
            {title}
        </Typography>
    )
};

const SectionData = ({ data }) => {
    return (
        <Typography style={{
            fontSize: '1rem',
            fontWeight: '400',
            letterSpacing: '-0.5px',
            fontFamily: 'Inter, sans-serif',
            lineHeight: '1.41',
            color: '#757575',
            marginBottom: '16px',
        }}>
            {data}
        </Typography>
    )
};

const SectionAiGenerated = ({ isAiGenerated, isPartiallyAiGenerated }) => {
    const { t } = useTranslation();
    if (isAiGenerated) {
        return (
            <Box style={{
                backgroundColor: '#F3FCFC',
                padding: '16px',
                marginBottom: '16px',
            }} >
                {isPartiallyAiGenerated
                    ? t('aiGeneratedPartially')
                    : t('aiGenerated')}
            </Box>
        );
    }

    return (<></>);
};

const SocialMedias = ({ socialMedia }) => {
    return (
        <Box marginBottom={'16px'}>
            {socialMedia.map((social) => (
                <Row key={getDomainName(social)}>
                    <Column>
                        <Box padding={'2px'} paddingRight={'6px'}>
                            <img src={getLogoUrl(getDomainName(social).toLowerCase())} width={'16px'} height={'16px'} />
                        </Box>
                    </Column>

                    <Column>
                        <Link href={social} target="_blank" rel="noopener noreferrer" style={{
                            fontSize: '1rem',
                            fontWeight: '400',
                            letterSpacing: '-0.5px',
                            fontFamily: 'Inter, sans-serif',
                            lineHeight: '1.41',
                            color: '#0164DB',
                            textDecoration: 'none',
                        }}>
                            {getUserNameFromUrl(social)}
                        </Link>
                        <Typography style={{
                            fontSize: '1rem',
                            fontWeight: '400',
                            letterSpacing: '-0.5px',
                            fontFamily: 'Inter, sans-serif',
                            lineHeight: '1.41',
                            color: '#757575',
                            textDecoration: 'none',
                        }}>
                            {getDomainName(social)}
                        </Typography>
                    </Column>
                </Row>
            ))}
        </Box>
    );

}

const Actions = ({ actions }) => {
    return (
        <Box marginBottom={'16px'}>
            {actions.map((action) => (
                <Row key={action.description}>
                    <Column>
                        <Typography style={{
                            fontSize: '1rem',
                            fontWeight: '400',
                            letterSpacing: '-0.5px',
                            fontFamily: 'Inter, sans-serif',
                            lineHeight: '1.41',
                            color: '#757575',
                        }}>
                            {action.label}
                        </Typography>
                    </Column>
                </Row>
            ))}
        </Box>
    );
}

const Ingredients = ({ ingredients }) => {
    if (!ingredients) return <></>;

    return (
        <Card style={{ border: '1px solid #d8d8d8', marginTop: '16px', marginBottom: '24px' }}>
            <Box>
                {ingredients.map((ingredient) => (
                    <Row key={ingredient}>
                        <img src={ingredient.url} width={'80px'} height={'auto'} style={{ padding: '8px' }} />
                        <Column style={{
                            justifyContent: 'center',
                            alignItems: 'baseline',
                            height: 'auto',
                        }}
                            sx={{
                                maxWidth: 'calc(100vw - 96px - 32px - 92px)',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            <Typography
                                style={{
                                    fontSize: '1rem',
                                    fontWeight: '700',
                                    letterSpacing: '-0.5px',
                                    fontFamily: 'Inter, sans-serif',
                                    lineHeight: '1.41',
                                    color: '#000',
                                    overflow: 'hidden',        // Ensure overflow is hidden
                                    whiteSpace: 'nowrap',      // Prevent text wrapping
                                    textOverflow: 'ellipsis',  // Show ellipsis for overflowed text
                                    display: 'inline-block',   // Make sure this is inline-block or block
                                    maxWidth: '100%',          // Ensure there's a defined max-width
                                }}
                            >
                                {ingredient.name}
                            </Typography>

                            <Typography style={{
                                fontSize: '0.8rem',
                                fontWeight: '400',
                                letterSpacing: '-0.5px',
                                fontFamily: 'Inter, sans-serif',
                                lineHeight: '1.41',
                                color: '#757575',
                            }}>
                                {ingredient.timestamp.includes('Invalid') ? 'No Data' : ingredient.timestamp}
                            </Typography>
                        </Column>
                    </Row>
                ))}
            </Box>
        </Card>
    );
};

const DisplayContentCredentials = ({ manifest, source }) => {
    const { t, i18n } = useTranslation();
    const activeManifestID = manifest.activeManifest.label;

    const [issuer, setIssuer] = useState('');
    const [time, setTime] = useState('');
    const [producerName, setProducerName] = useState('');
    const [aiToolUsed, setAiToolUsed] = useState('');
    const [appOrDeviceUsed, setAppOrDeviceUsed] = useState('');
    const [isAiGenerated, setIsAiGenerated] = useState(false);
    const [isPartiallyAiGenerated, setIsPartiallyAiGenerated] = useState(false);
    const [socialMedia, setSocialMedia] = useState([]);
    const [actions, setActions] = useState([]);
    const [thumbnails, setThumbnails] = useState([]);

    useEffect(async () => {
        if (!manifest || !manifest.manifests?.[activeManifestID]) return;

        try {
            // Set Issuer
            const issuerValue = manifest.manifests?.[activeManifestID]?.signatureInfo?.issuer;
            setIssuer(issuerValue || t('unknown'));
        } catch {
            setIssuer(t('unknown'));
        }

        try {
            // Set Time
            const timeValue = manifest.manifests?.[activeManifestID]?.signatureInfo?.time;
            if (timeValue) {
                const date = new Date(timeValue);
                const formattedDate = `${date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })} ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })} GMT${(date.getTimezoneOffset() > 0 ? '-' : '+')}${Math.abs(date.getTimezoneOffset() / 60).toString().padStart(2, '0')}:00`;
                setTime(formattedDate);
            } else {
                setTime(t('undefined'));
            }
        } catch {
            setTime(t('undefined'));
        }

        try {
            // console.log('selectGenerativeInfo', selectGenerativeInfo(manifest.manifests[activeManifestID]));

            // Detect AI Generated and Partially AI Generated Content
            let isAi = false;
            let isPartiallyAi = false;
            let counterAssertions = 0;
            let counterActions = 0;

            const assertions = manifest.manifests?.[activeManifestID]?.assertions.data || [];
            for (let i = 0; i < assertions.length; i++) {
                const actions = assertions[i]?.data?.actions || [];

                for (let j = 0; j < actions.length; j++) {
                    const digitalSourceType = actions[j]?.digitalSourceType || '';
                    if (digitalSourceType.includes('compositeWithTrainedAlgorithmicMedia')) {
                        isAi = true;
                        isPartiallyAi = true;
                        counterAssertions = i;
                        counterActions = j;
                        break;
                    } else if (digitalSourceType.includes('trainedAlgorithmicMedia')) {
                        isAi = true;
                        counterAssertions = i;
                        counterActions = j;
                        break;
                    }
                }
                if (isAi) break;
            }

            setIsAiGenerated(isAi);
            setIsPartiallyAiGenerated(isPartiallyAi);

            // Set AI Tool Used
            const softwareAgent =
                isAi &&
                assertions?.[counterAssertions]?.data?.actions?.[counterActions]?.softwareAgent;
            setAiToolUsed(softwareAgent || '');
        } catch (err) {
            setIsAiGenerated(false);
            setIsPartiallyAiGenerated(false);
            console.log('error', err);
        }

        try {
            // Set Producer Name
            const producer = selectProducer(manifest.manifests?.[activeManifestID])
            setProducerName(producer.name || '');
        } catch {
            setProducerName('');
        }

        try {
            // Set App or Device Used
            // console.log(parseGenerator(manifest.manifests?.[activeManifestID].claimGenerator))
            // TODO use above
            const appOrDevice = manifest.manifests?.[activeManifestID]?.claimGenerator?.split(' ')[0];
            setAppOrDeviceUsed(appOrDevice || '');
        } catch {
            setAppOrDeviceUsed('');
        }

        try {
            // Set Social Media
            const socialMediaArray = selectSocialAccounts(manifest.manifests?.[activeManifestID]).map((author) => author['@id']).filter((id) => id);
            setSocialMedia(socialMediaArray);
        } catch (err) {
            setSocialMedia([]);
            console.log('error', err);
        }

        try {
            const assertions = manifest.manifests?.[activeManifestID]?.assertions.data;
            const actionsObject = assertions.find(item => item.label === 'c2pa.actions');
            const uniqueActions = getC2paCategorizedActions(actionsObject, i18n.language);
            setActions(uniqueActions);
        } catch (err) {
            setActions([]);
            console.log('error', err);
        }

        try {
            // Set Thumbnails

            const ingredients = manifest.manifests?.[activeManifestID]?.ingredients;
            const ingredientsThumbnails = [];



            ingredients.forEach(element => {
                const { url } = element.thumbnail.getUrl();
                const date = new Date(element?.manifest?.signatureInfo?.time);
                const formattedDate = `${date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })} ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })} GMT${(date.getTimezoneOffset() > 0 ? '-' : '+')}${Math.abs(date.getTimezoneOffset() / 60).toString().padStart(2, '0')}:00`;
                ingredientsThumbnails.push({
                    url: url,
                    name: element?.title || t('unknown'),
                    timestamp: formattedDate || t('unknown')
                });
            });

            setThumbnails(ingredientsThumbnails);
        } catch (err) {
            setThumbnails([]);
            console.log('error', err);
        }

    }, [manifest, activeManifestID]);

    return (
        <Box>

            {isAiGenerated && <SectionAiGenerated isAiGenerated={isAiGenerated} isPartiallyAiGenerated={isPartiallyAiGenerated} />}

            {issuer && (
                <>
                    <SectionTitle title={t('signedBy')} />
                    <SectionData data={issuer} />
                </>
            )}

            {time && (
                <>
                    <SectionTitle title={t('signedOn')} />
                    <SectionData data={time} />
                </>
            )}

            {producerName && (
                <>
                    <SectionTitle title={t('producedBy')} />
                    <SectionData data={producerName} />
                </>
            )}

            {aiToolUsed && (
                <>
                    <SectionTitle title={t('aiToolUsed')} />
                    <SectionData data={aiToolUsed} />
                </>
            )}

            {appOrDeviceUsed && (
                <>
                    <SectionTitle title={t('appOrDeviceUsed')} />
                    <SectionData data={appOrDeviceUsed} />
                </>
            )}

            {socialMedia && socialMedia.length > 0 && (
                <>
                    <SectionTitle title={t('socialMedia')} />
                    <SocialMedias socialMedia={socialMedia} />
                </>
            )}

            {actions && actions.length > 0 && (
                <>
                    <SectionTitle title={t('actions')} />
                    <Actions actions={actions} />
                </>
            )}

            {thumbnails && thumbnails.length > 0 && (
                <>
                    <SectionTitle title={t('ingredients')} />
                    <Ingredients ingredients={thumbnails} />
                </>
            )}

        </Box>
    );
};

export default DisplayContentCredentials;
